import type { RecordedOperatorStep } from '@erp/rpc';
import type { OperatorStepAction } from '@erp/shared-types';
import type { Span } from './types';

/** One identity in both the action rail and full trace; indices can restart. */
export function operatorActionId(
  recordId: string,
  actionIndex: number,
): string {
  return `operator:${recordId}:action:${actionIndex}`;
}

function canonicalSpan(
  row: RecordedOperatorStep,
  start: number,
  title: (action: OperatorStepAction) => string,
  projection?: Span,
): Span {
  const base: Span = {
    ...generationMetrics(row),
    ...projection,
    id: `operator:${row.id}`,
    type: 'tool',
    name: `Step ${row.step.index}`,
    status: row.step.status === 'error' ? 'failed' : 'completed',
    startMs:
      projection?.startMs ?? Math.max(0, row.createdAt.getTime() - start),
    durMs: null,
    reasoning: row.step.generation.reasoning,
    thinking: row.step.generation.thinking ?? projection?.thinking ?? null,
    operatorEvidence: { record: row },
  };
  if (row.step.generation.modelFree) base.model = null;
  const actions = row.step.actions.map((action, index): Span => ({
    id: operatorActionId(row.id, index),
    type: 'tool',
    name: title(action),
    detail: null,
    action:
      typeof action.args?.action === 'string'
        ? action.args.action
        : action.tool,
    status: action.result.status === 'error' ? 'failed' : base.status,
    startMs: base.startMs,
    durMs: null,
    toolInput: action.args ?? {},
    toolOutput: { ...action.result },
    screenshotSignedUrl: row.screenshotUrls[index],
    reasoning: row.step.generation.reasoning,
    thinking: base.thinking,
    operatorEvidence: { record: row, actionIndex: index },
  }));
  // One-action generations need no extra row. Retain generation accounting on that action.
  if (actions.length === 1)
    return { ...base, ...actions[0], projectionEvidence: projection };
  return {
    ...base,
    id: `operator:${row.id}`,
    operatorEvidence: { record: row },
    projectionEvidence: projection,
    children: [...actions, ...(projection?.children ?? [])],
  };
}

/** Match only durable evidence identity, never timestamps, labels or reset step numbers.
 * Unmatched legacy spans are preserved rather than guessed away. */
export function mergeOperatorEvidence(
  spans: Span[],
  rows: RecordedOperatorStep[],
  runStart: number,
  title: (action: OperatorStepAction) => string,
): Span[] {
  if (rows.length === 0) return spans;
  const byFrame = new Map<string, RecordedOperatorStep[]>();
  for (const row of rows) {
    const frame = row.step.actions[0]?.result.screenshotRef;
    if (!frame) continue;
    const list = byFrame.get(frame) ?? [];
    list.push(row);
    byFrame.set(frame, list);
  }
  const used = new Set<string>();
  function attach(list: Span[]): Span[] {
    return list.map((span) => {
      const frame = span.toolOutput?.screenshotDocumentId;
      const matches =
        typeof frame === 'string' ? byFrame.get(frame) : undefined;
      const match = matches?.length === 1 ? matches[0] : undefined;
      if (match && !used.has(match.id)) {
        used.add(match.id);
        return canonicalSpan(match, runStart, title, span);
      }
      return span.children
        ? { ...span, children: attach(span.children) }
        : span;
    });
  }
  const merged = attach(spans);
  const remaining = rows.filter((row) => !used.has(row.id));
  return [
    ...merged,
    ...remaining.map((row) => canonicalSpan(row, runStart, title)),
  ].sort((left, right) => left.startMs - right.startMs);
}

/** A task-loop phase, or one step of the workflow a phase launched: it stays a fold-out group in
 * the Actions view, holding the actions that happened inside it. */
function isActionGroup(span: Span): boolean {
  return span.isPhase === true || span.isWorkflowStep === true;
}

/**
 * The Actions view's tree: what the run did, action by action, inside the phases that did it.
 *
 * Everything else the full trace wraps actions in (LLM turns, subagent calls) is flattened away.
 * Phases and workflow steps are kept as groups — flattening them too turned setup and teardown work
 * into loose actions indistinguishable from the task's. A group is a copy with its children
 * projected the same way; its id is the original's, so selection is still shared with the full
 * trace. Actions outside any phase keep their place, and every level is in time order.
 */
export function recordedActionSpans(spans: Span[]): Span[] {
  return adoptIntoGroups(projectActions(spans));
}

/** Does this group's recorded window cover a moment? A running group is still open-ended. */
function groupCovers(group: Span, atMs: number): boolean {
  const end =
    group.status === 'running'
      ? Number.POSITIVE_INFINITY
      : group.startMs +
        (group.timedDurMs ?? group.durMs ?? Number.POSITIVE_INFINITY);
  return atMs >= group.startMs && atMs <= end;
}

/**
 * The group an action happened in. Phases run one after another, so when more than one window
 * covers the moment — an open-ended running phase, or one stretched over the work nested in it —
 * the latest to START is the one that was actually running then.
 */
function coveringGroup(groups: Span[], span: Span): Span | undefined {
  let home: Span | undefined;
  for (const group of groups) {
    if (!groupCovers(group, span.startMs)) continue;
    if (!home || group.startMs > home.startMs) home = group;
  }
  return home;
}

/**
 * Move an action that landed outside every group into the group whose window it happened in.
 *
 * `mergeOperatorEvidence` appends a canonical record it could not match to a projection row at the
 * TOP of the tree — so a login step's unmatched action would show up as a loose row beside the
 * setup phase it ran in. Placed by time, the only link such a row has; an action inside no group's
 * window stays where it is.
 */
function adoptIntoGroups(level: Span[]): Span[] {
  const groups = level.filter(isActionGroup);
  const kept: Span[] = [];
  for (const span of level) {
    const home = isActionGroup(span) ? undefined : coveringGroup(groups, span);
    if (home) home.children = [...(home.children ?? []), span];
    else kept.push(span);
  }
  for (const group of groups) {
    group.children = adoptIntoGroups(group.children ?? []).sort(
      compareRecordedActions,
    );
  }
  return kept;
}

function projectActions(spans: Span[]): Span[] {
  return spans
    .flatMap((span) => {
      if (isActionGroup(span)) {
        return [{ ...span, children: projectActions(span.children ?? []) }];
      }
      if (span.operatorEvidence?.actionIndex !== undefined) return [span];
      // A replayed procedure (run_sop) writes no canonical operator records — its steps, the
      // healer's actions and its reasoning live only on the procedure's own rows. Without this the
      // Actions view skipped it entirely, which on a task loop is most of what the run did. It
      // enters whole (procedure → step → action) so its steps stay grouped; a procedure the
      // operator replayed inline DOES carry canonical records, and those keep their own entries.
      if (span.isSopProcedure && !hasCanonicalEvidence(span)) return [span];
      if (span.children?.length) return projectActions(span.children);
      return span.operatorEvidence ? [span] : [];
    })
    .sort(compareRecordedActions);
}

/** The entries the Actions view steps through (Previous / Next): every action and procedure, with
 * the phase and workflow-step groups around them opened up. */
export function recordedActionEntries(tree: Span[]): Span[] {
  return tree.flatMap((span) =>
    isActionGroup(span) ? recordedActionEntries(span.children ?? []) : [span],
  );
}

function hasCanonicalEvidence(span: Span): boolean {
  return (
    span.operatorEvidence !== undefined ||
    (span.children?.some(hasCanonicalEvidence) ?? false)
  );
}

function generationMetrics(row: RecordedOperatorStep) {
  return {
    model: row.step.generation.modelId,
    inputTokens: row.step.generation.tokens?.input,
    outputTokens: row.step.generation.tokens?.output,
    cacheReadTokens: row.step.generation.tokens?.cacheRead,
    cacheWriteTokens: row.step.generation.tokens?.cacheWrite,
  };
}

function compareRecordedActions(left: Span, right: Span): number {
  const a = left.operatorEvidence;
  const b = right.operatorEvidence;
  // A procedure has no record to order by; both sides share the run's clock, so start time places
  // it among the canonical actions.
  if (!a || !b) return left.startMs - right.startMs;
  return (
    a.record.createdAt.getTime() - b.record.createdAt.getTime() ||
    a.record.id.localeCompare(b.record.id) ||
    (a.actionIndex ?? 0) - (b.actionIndex ?? 0)
  );
}
